import { createApi } from '@reduxjs/toolkit/query/react';
import { filteredFilter } from '../../utils/api';
import {properties} from "../../properties.js";
import { authBaseQuery } from '../../app/axiosBaseQuery';
import { PaginationResult, ListRequest } from '../../types/global.d';
import * as Entity from '../types/models/ThicknessClass';
import {createNormalizationMiddleware} from "../../app/normy/normyRtkQuery.ts";

export const THICKNESS_CLASS_API_REDUCER_KEY = 'thickness-classes';
export const THICKNESS_CLASS_PATH = 'thickness-classes';

export const thicknessClassApi = createApi({
    reducerPath: THICKNESS_CLASS_API_REDUCER_KEY,
    baseQuery: authBaseQuery(),
    keepUnusedDataFor: 10,
    refetchOnMountOrArgChange: 10,
    refetchOnFocus: true,
    tagTypes: ['ThicknessClass', 'ThicknessClasses'],
    endpoints: (builder) => ({
        getThicknessClassList: builder.query<PaginationResult<Entity.ThicknessClass>, ListRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request))
                return ({
                    url: `/${ THICKNESS_CLASS_PATH }?${new URLSearchParams(queryParams).toString()}`,
                    method: 'GET',
                })
            },
            providesTags: ['ThicknessClasses'],
        }),
        getThicknessClass: builder.query<Entity.ThicknessClass, Entity.PrimaryThicknessClass>({
            queryFn: async (id, { getState }, extraOptions, baseQuery) => {

                const cachedData = thicknessClassNormalizer.getObjectById(id.id);

                if (cachedData) {
                    return { data: cachedData };
                } else {
                    const response = await baseQuery({
                        url: `/${ THICKNESS_CLASS_PATH }/${id.toPath()}`,
                        method: 'GET',
                    });

                    return response;
                }
            },
            providesTags: ['ThicknessClass'],
        }),
        getThicknessClassMultiple: builder.query<Entity.ThicknessClass, Entity.PrimaryThicknessClass[]>({
            query: (ids) => {
                return ({
                    url: `/${ THICKNESS_CLASS_PATH }/ids${Entity.PrimaryThicknessClass.toQueryParams(ids)}`,
                    method: 'GET',
                })
            },
            providesTags: ['ThicknessClasses'],
        }),
        getThicknessClassPresentation: builder.query<PaginationResult<Entity.ThicknessClass>, Entity.PresentationThicknessClassRequest>({
            query: (request) => {
              const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
              return ({
                url: `/${ THICKNESS_CLASS_PATH }/presentation?${new URLSearchParams(queryParams).toString()}`,
                method: 'POST',
                data: request.filter,
              })
            },
            providesTags: ['ThicknessClasses'],
        }),
        getThicknessClassMinMax: builder.query<PaginationResult<Entity.ThicknessClass>, Entity.FilterMinMaxRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
                return ({
                    url: `/${ THICKNESS_CLASS_PATH }/find/minMax?${new URLSearchParams(queryParams).toString()}`,
                    method: 'POST',
                    data: request.filter,
                })
            },
            providesTags: ['ThicknessClasses'],
        }),
        
        insertThicknessClass: builder.mutation<Entity.ThicknessClass, Entity.CreateThicknessClass>({
            query: (toInsert) => {
                return ({
                    url: `/${ THICKNESS_CLASS_PATH }`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                dispatch(
                    thicknessClassApi.util.upsertQueryData('getThicknessClass', Entity.PrimaryThicknessClass.fromId(mutationData.id), mutationData),
                );
            },
            invalidatesTags: ['ThicknessClasses'],
        }),
        insertMultipleThicknessClass: builder.mutation<Entity.ThicknessClass[], Entity.CreateThicknessClass[]>({
            query: (toInsert) => {
                return ({
                    url: `/${ THICKNESS_CLASS_PATH }/batch`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                for(const item of mutationData) {
                    dispatch(
                            thicknessClassApi.util.upsertQueryData('getThicknessClass', Entity.PrimaryThicknessClass.fromId(item.id), item),
                    );
                }
            },
            invalidatesTags: ['ThicknessClasses'],
        }),
        updateThicknessClass: builder.mutation<Entity.ThicknessClass, Entity.UpdateEntityThicknessClass>({
            query: (toUpdate) => {
                return ({
                    url: `/${ THICKNESS_CLASS_PATH }/${toUpdate.id.toPath()}`,
                    method: 'PUT',
                    data: toUpdate.data,
                })
            },
            invalidatesTags: ['ThicknessClasses'],
        }),
        updateMultipleThicknessClass: builder.mutation<Entity.ThicknessClass[], Entity.UpdateEntityThicknessClass[]>({
            query: (toUpdate) => {
                return ({
                    url: `/${ THICKNESS_CLASS_PATH }/batch`,
                    method: 'PUT',
                    data: toUpdate,
                })
            },
            invalidatesTags: ['ThicknessClasses'],
        }),
        deleteThicknessClass: builder.mutation<Entity.ThicknessClass, Entity.ThicknessClass>({
            query: (toDelete) => {
                return ({
                    url: `/${ THICKNESS_CLASS_PATH }/${Entity.PrimaryThicknessClass.fromEntity(toDelete)?.toPath()}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['ThicknessClasses'],
        }),
        deleteMultipleThicknessClass: builder.mutation<Entity.ThicknessClass[], Entity.PrimaryThicknessClass[]>({
            query: (ids) => {
                return ({
                    url: `/${ THICKNESS_CLASS_PATH }/batch${Entity.PrimaryThicknessClass.toQueryParams(ids)}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['ThicknessClasses'],
        }),
    }),
})

export const [thicknessClassNormalizer, thicknessClassNormalizerMiddleware] = createNormalizationMiddleware(THICKNESS_CLASS_API_REDUCER_KEY, thicknessClassApi, {
    devLogging: properties.debugLog,
})

export const {
    useGetThicknessClassListQuery,
    useGetThicknessClassMultipleQuery,
    useGetThicknessClassPresentationQuery,
    useGetThicknessClassQuery,
    useInsertThicknessClassMutation,
    useInsertMultipleThicknessClassMutation,
    useUpdateThicknessClassMutation,
    useUpdateMultipleThicknessClassMutation,
    useDeleteThicknessClassMutation,
    useDeleteMultipleThicknessClassMutation,
    useGetThicknessClassMinMaxQuery,
    
} = thicknessClassApi
